import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import HomePage from "@/pages/HomePage";
import MenuPage from "@/pages/MenuPage";
import OrderPage from "@/pages/OrderPage";
import BookingPage from "@/pages/BookingPage";
import GiftCardPage from "@/pages/GiftCardPage";
import CateringPage from "@/pages/CateringPage";
import GalleryPage from "@/pages/GalleryPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import TrackOrderPage from "@/pages/TrackOrderPage";
import AdminPage from "@/pages/AdminPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminMenuPage from "@/pages/AdminMenuPage";
import AdminGalleryPage from "@/pages/AdminGalleryPage";
import AdminReviewsPage from "@/pages/AdminReviewsPage";
import AdminGiftCardPage from "@/pages/AdminGiftCardPage";
import AdminCateringPage from "@/pages/AdminCateringPage";
import AdminAboutPage from "@/pages/AdminAboutPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import KitchenDashboard from "@/pages/KitchenDashboard";
import KitchenLoginPage from "@/pages/KitchenLoginPage";
import ReportsPage from "@/pages/ReportsPage";
import PaymentFailedPage from "@/pages/PaymentFailedPage";
import GiftCardSuccessPage from "@/pages/GiftCardSuccessPage";
import GiftCardBalancePage from "@/pages/GiftCardBalancePage";
import TrafficPage from "@/pages/TrafficPage";
import { PageTracker } from "@/components/PageTracker";
import KitchenTicketPage from "@/pages/KitchenTicketPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import GDPRPage from "@/pages/GDPRPage";
import AuthCallback from "@/pages/AuthCallback";
import CookieConsent from "@/components/CookieConsent";
import { Toaster } from "@/components/ui/sonner";
import { Phone } from "lucide-react";
import { AdminAuthProvider, ProtectedAdminRoute } from "@/context/AdminAuthContext";
import { Navigate } from "react-router-dom";

// Protected Kitchen Route - uses kitchen_token
const ProtectedKitchenRoute = ({ children }) => {
  const [checking, setChecking] = React.useState(true);
  const [authenticated, setAuthenticated] = React.useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem("kitchen_token") || localStorage.getItem("admin_token");
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/kitchen/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data.valid) setAuthenticated(true); setChecking(false); })
      .catch(() => { localStorage.removeItem("kitchen_token"); setChecking(false); });
  }, []);

  if (checking) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}>
      <div className="w-16 h-16 border-4 rounded-full animate-spin" style={{ borderColor: '#d4af37', borderTopColor: 'transparent' }}></div>
    </div>
  );
  if (!authenticated) return <Navigate to="/kjokkenet/login" replace />;
  return children;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const CartContext = createContext();

export const useCart = () => useContext(CartContext);

import { LanguageProvider } from "@/LanguageContext";

// Floating Phone Button Component
const FloatingPhoneButton = () => {
  return (
    <a
      href="tel:+4748915190"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full shadow-2xl hover:scale-105 transition-transform duration-300"
      style={{ 
        background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)',
        border: '2px solid #d4af37',
        color: '#d4af37'
      }}
      data-testid="floating-phone-button"
    >
      <Phone className="h-5 w-5 animate-pulse" />
      <span className="font-semibold text-sm hidden sm:inline">Ring Oss</span>
    </a>
  );
};

function App() {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("masala_cart");
    return saved ? JSON.parse(saved) : [];
  });
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardDiscount, setGiftCardDiscount] = useState(0);

  useEffect(() => {
    localStorage.setItem("masala_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item) => {
    setCart((prevCart) => {
      const cartKey = item.cart_key || item.id;
      const unitPrice = item.unit_price || item.price;

      const existingItem = prevCart.find((i) => i.cart_key === cartKey);
      if (existingItem) {
        return prevCart.map((i) =>
          i.cart_key === cartKey
            ? { ...i, quantity: i.quantity + 1, total_price: (i.quantity + 1) * i.unit_price }
            : i
        );
      }
      return [
        ...prevCart,
        {
          menu_item_id: item.id,
          cart_key: cartKey,
          name: item.name,
          quantity: 1,
          unit_price: unitPrice,
          total_price: unitPrice,
          meat_choice: item.meat_choice || null,
          spice_level: item.spice_level || null,
        },
      ];
    });
  };

  const removeFromCart = (cartKey) => {
    setCart((prevCart) => prevCart.filter((i) => i.cart_key !== cartKey));
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((i) =>
        i.cart_key === cartKey
          ? { ...i, quantity, total_price: quantity * i.unit_price }
          : i
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setGiftCardCode("");
    setGiftCardDiscount(0);
    localStorage.removeItem("masala_cart");
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.total_price, 0);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const AppRouter = () => {
    const location = useLocation();
    // Check URL fragment for session_id synchronously during render
    if (location.hash?.includes('session_id=')) {
      return <AuthCallback />;
    }
    return (
      <>
      <PageTracker />
      <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/order" element={<OrderPage />} />
            <Route path="/bestill" element={<OrderPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/gift-cards" element={<GiftCardPage />} />
            <Route path="/gift-cards/saldo" element={<GiftCardBalancePage />} />
            <Route path="/catering" element={<CateringPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/track/:orderNumber" element={<TrackOrderPage />} />
            <Route path="/panel050623/login" element={<AdminLoginPage />} />
            <Route path="/panel050623" element={<ProtectedAdminRoute><AdminPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/settings" element={<ProtectedAdminRoute><AdminSettingsPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/reports" element={<ProtectedAdminRoute><ReportsPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/menu" element={<ProtectedAdminRoute><AdminMenuPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/gallery" element={<ProtectedAdminRoute><AdminGalleryPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/reviews" element={<ProtectedAdminRoute><AdminReviewsPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/giftcards" element={<ProtectedAdminRoute><AdminGiftCardPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/traffic" element={<ProtectedAdminRoute><TrafficPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/catering" element={<ProtectedAdminRoute><AdminCateringPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/about" element={<ProtectedAdminRoute><AdminAboutPage /></ProtectedAdminRoute>} />
            <Route path="/panel050623/kitchen" element={<ProtectedAdminRoute><KitchenDashboard /></ProtectedAdminRoute>} />
            <Route path="/panel050623/kitchen-ticket/:orderNumber" element={<ProtectedAdminRoute><KitchenTicketPage /></ProtectedAdminRoute>} />
            <Route path="/kjokkenet/login" element={<KitchenLoginPage />} />
            <Route path="/kjokkenet" element={<ProtectedKitchenRoute><KitchenDashboard /></ProtectedKitchenRoute>} />
            <Route path="/kjokkenet/ticket/:orderNumber" element={<ProtectedKitchenRoute><KitchenTicketPage /></ProtectedKitchenRoute>} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment-failed" element={<PaymentFailedPage />} />
            <Route path="/gift-card-success" element={<GiftCardSuccessPage />} />
            <Route path="/personvern" element={<PrivacyPolicyPage />} />
            <Route path="/mine-rettigheter" element={<GDPRPage />} />
      </Routes>
      </>
    );
  };

  return (
    <LanguageProvider>
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
        giftCardCode,
        setGiftCardCode,
        giftCardDiscount,
        setGiftCardDiscount,
      }}
    >
      <AdminAuthProvider>
      <div className="App min-h-screen">
        <BrowserRouter>
          <AppRouter />
          <FloatingPhoneButton />
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
      </AdminAuthProvider>
    </CartContext.Provider>
    </LanguageProvider>
  );
}

export default App;
